import * as React from 'react'
import { NavLink, Outlet, Link, useLocation } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { Drawer, DrawerContent, DrawerTrigger } from '#app/components/ui/drawer.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Logo } from '#app/components/ui/logo.tsx'
import { cn } from '#app/utils/misc.tsx'

// EDIT HERE: List of guide sections and their items
// Add your guide routes here. The 'to' prop should be the path relative to /guias
const guideSections = [
	{
		title: 'General',
		items: [{ title: 'Introducción', to: 'introduccion' }],
	},
	{
		title: 'Asistente Auri',
		items: [
			{ title: 'Como usar', to: 'auri-como-usar' },
			{ title: 'Qué son las instrucciones', to: 'auri-instrucciones-que-es' },
			{ title: 'Subir documentos', to: 'auri-subir-documentos' },
		],
	},
	{
		title: 'Clientes',
		items: [
			{ title: 'Qué es un cliente', to: 'proyectos-que-es' },
			{ title: 'Como usar clientes', to: 'proyectos-como-usar' },
			{ title: 'Compartir clientes', to: 'proyectos-compartir-proyectos' },
			{ title: 'Clientes y expedientes', to: 'proyectos-y-expedientes' },
		],
	},
	{
		title: 'Buscador Legal',
		items: [{ title: 'Como usar', to: 'buscador-como-usar' }],
	},
	{
		title: 'Análisis de expedientes',
		items: [
			{ title: 'Como usar', to: 'analisis-expedientes-como-usar' },
			{
				title: 'Subir un expediente',
				to: 'analisis-expedientes-subir-documentos',
			},
			{
				title: 'Compartir expedientes',
				to: 'analisis-expedientes-compartir-expedientes',
			},
		],
	},
	{
		title: 'Transcripciones',
		items: [{ title: 'Como usar', to: 'transcripciones-como-usar' }],
	},
	{
		title: 'Redactor',
		items: [
			{ title: 'Como usar', to: 'redactor-como-usar' },
			{ title: 'Usar clientes en el redactor', to: 'redactor-usar-proyectos' },
			{
				title: 'Personalizar la redacción',
				to: 'redactor-personalizar-redaccion',
			},
		],
	},
	{
		title: 'Tablas IA',
		items: [{ title: 'Como usar', to: 'tablas-ia-como-usar' }],
	},
	{
		title: 'Agentes',
		items: [
			{ title: 'Qué es un agente', to: 'agentes-que-es' },
			{ title: 'Cuándo usarlo', to: 'agentes-cuando-usarlo' },
			{ title: 'Como crear un agente', to: 'agentes-como-crear' },
			{ title: 'Cómo funciona cada paso', to: 'agentes-como-funciona-cada-paso' },
			{ title: 'Ejecutar y leer resultados', to: 'agentes-ejecutar-y-leer-resultados' },
			{ title: 'Compartir y publicar', to: 'agentes-compartir-y-publicar' },
			{ title: 'Buenas prácticas', to: 'agentes-buenas-practicas' },
		],
	},
	{
		title: 'Exámenes',
		items: [
			{ title: 'Qué es y para qué funciona', to: 'examenes-que-es' },
			{ title: 'Practicar con un examen', to: 'examenes-practicar' },
			{ title: 'Compartir exámenes', to: 'examenes-compartir' },
		],
	},
	{
		title: 'Bases de conocimiento',
		items: [{ title: 'Como usar', to: 'bases-de-conocimiento-como-usar' }],
	},
	{
		title: 'Playbooks',
		items: [
			{ title: 'Conceptos básicos', to: 'revisiones-conceptos-basicos' },
			{ title: 'Como crear un playbook', to: 'revisiones-como-crear-manual' },
			{ title: 'Importar playbook', to: 'revisiones-importar-manual' },
			{ title: 'Compartir playbook con usuarios', to: 'revisiones-compartir-manual' },
			{ title: 'Revisar documentos por lote', to: 'revisiones-revisar-documentos-por-lote' },
		],
	},
	{
		title: 'Notificaciones',
		items: [{ title: 'Como usar', to: 'notificaciones-como-usar' }],
	},
	{
		title: 'Configuración',
		items: [
			{ title: 'Comprar paquetes adicionales', to: 'configuracion-comprar-paquetes' },
			{ title: 'Personalizar el asistente', to: 'configuracion-personalizar-asistente' },
			{ title: 'Plantillas de Word', to: 'configuracion-plantillas-word' },
			{ title: 'Historial de facturación', to: 'configuracion-historial-facturacion' },
			{ title: 'Facturas electrónicas', to: 'configuracion-facturas-electronicas' },
			{ title: 'Conexiones MCP', to: 'configuracion-conexiones-mcp' },
		],
	},
]

export default function GuidesLayout() {
	const [openDrawer, setOpenDrawer] = React.useState(false)
	const location = useLocation()
	const mainRef = React.useRef<HTMLElement>(null)

	// Flatten all guide items into a single array with section info
	const allGuides = React.useMemo(() => {
		return guideSections.flatMap(section =>
			section.items.map(item => ({
				...item,
				section: section.title,
			})),
		)
	}, [])

	// Find current, previous, and next guides
	const { currentGuide, prevGuide, nextGuide } = React.useMemo(() => {
		const currentPath = location.pathname.replace('/guias/', '')
		const currentIndex = allGuides.findIndex(guide => guide.to === currentPath)

		return {
			currentGuide: currentIndex >= 0 ? allGuides[currentIndex] : null,
			prevGuide: currentIndex > 0 ? allGuides[currentIndex - 1] : null,
			nextGuide: currentIndex >= 0 && currentIndex < allGuides.length - 1 ? allGuides[currentIndex + 1] : null,
		}
	}, [location.pathname, allGuides])

	// close drawer when route changes
	React.useEffect(() => {
		setOpenDrawer(false)
	}, [location.pathname])

	// Scroll to top when route changes
	React.useEffect(() => {
		mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
	}, [location.pathname])

	const sidebarRef = React.useRef<HTMLDivElement>(null)

	// Scroll active sidebar item into view on route change
	React.useEffect(() => {
		const sidebar = sidebarRef.current
		if (!sidebar) return
		const activeLink = sidebar.querySelector('[data-active="true"]')
		if (activeLink) {
			activeLink.scrollIntoView({ block: 'center', behavior: 'smooth' })
		}
	}, [location.pathname])

	const SidebarContent = (
		<div ref={sidebarRef} className="custom-scrollbar flex h-full flex-col overflow-y-auto">
			{/* Brand header */}
			<div className="sticky top-0 z-10 border-b border-slate-100 bg-white/80 px-5 pb-4 pt-6 backdrop-blur-sm">
				<Link to="/" className="group flex items-center gap-2.5">
					<Logo className="h-7 w-7 transition-transform duration-300 group-hover:scale-105" />
					<div className="flex flex-col">
						<span className="font-instrument-serif text-[17px] font-normal tracking-tight text-slate-800">IUSPRO</span>
						<span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">Documentación</span>
					</div>
				</Link>
			</div>

			{/* Back link */}
			<div className="px-5 pt-4">
				<Link
					to="/"
					className="group inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[12px] font-medium text-slate-400 transition-all duration-200 hover:bg-slate-50 hover:text-slate-600"
				>
					<Icon name="arrow-left" className="h-3 w-3 transition-transform duration-200 group-hover:-translate-x-0.5" />
					Volver a la app
				</Link>
			</div>

			{/* Navigation sections */}
			<nav className="flex-1 px-3 pb-8 pt-4">
				{guideSections.map((section, index) => (
					<div key={section.title} className={cn(index !== 0 && 'mt-6')}>
						<div className="mb-1 flex items-center gap-2 px-3">
							<span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
								{section.title}
							</span>
							<div className="h-px flex-1 bg-gradient-to-r from-slate-100 to-transparent" />
						</div>
						<div className="flex flex-col gap-px">
							{section.items.map(guide => (
								<NavLink
									key={guide.to}
									to={guide.to}
									className={({ isActive }) =>
										cn(
											'group relative flex items-center rounded-lg px-3 py-[7px] text-[13px] transition-all duration-200',
											isActive
												? 'bg-primary/[0.06] font-medium text-primary'
												: 'text-slate-500 hover:bg-slate-50 hover:text-slate-700',
										)
									}
								>
									{({ isActive }) => (
										<>
											<span
												className={cn(
													'absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-full transition-all duration-300',
													isActive ? 'scale-y-100 bg-primary opacity-100' : 'scale-y-0 bg-transparent opacity-0',
												)}
											/>
											<span data-active={isActive} className="truncate">
												{guide.title}
											</span>
										</>
									)}
								</NavLink>
							))}
						</div>
					</div>
				))}
			</nav>
		</div>
	)

	return (
		<div className="flex h-full flex-col overflow-hidden bg-white text-foreground md:flex-row">
			{/* Mobile Header */}
			<div className="flex flex-shrink-0 items-center justify-between border-b border-slate-100 bg-white px-4 py-3 md:hidden">
				<Link to="/guias" className="flex items-center gap-2">
					<Logo className="h-6 w-6" />
					<span className="font-instrument-serif text-[15px] text-slate-800">IUSPRO</span>
					<span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
						Docs
					</span>
				</Link>
				<Drawer direction="left" open={openDrawer} onOpenChange={setOpenDrawer}>
					<DrawerTrigger asChild>
						<Button variant="ghost" size="sm" className="-mr-2 h-8 w-8 rounded-lg p-0">
							<Icon name="menu" className="h-[18px] w-[18px] text-slate-500" />
							<span className="sr-only">Menu</span>
						</Button>
					</DrawerTrigger>
					<DrawerContent className="w-[280px] bg-white">{SidebarContent}</DrawerContent>
				</Drawer>
			</div>

			{/* Desktop Sidebar */}
			<aside className="hidden h-full w-[260px] flex-shrink-0 flex-col border-r border-slate-100 bg-white md:flex">
				{SidebarContent}
			</aside>

			{/* Main Content */}
			<main ref={mainRef} className="custom-scrollbar h-full flex-1 overflow-y-auto bg-slate-50/40">
				{/* Breadcrumb bar */}
				{currentGuide && (
					<div className="sticky top-0 z-10 border-b border-slate-100 bg-white/70 backdrop-blur-md">
						<div className="mx-auto flex max-w-4xl items-center gap-2 px-6 py-2.5 lg:px-12">
							<Link
								to="/guias/introduccion"
								className="text-[12px] font-medium text-slate-400 transition-colors hover:text-slate-600"
							>
								Guías
							</Link>
							<Icon name="chevron-down" className="h-3 w-3 -rotate-90 text-slate-300" />
							<span className="text-[12px] font-medium text-slate-400">{currentGuide.section}</span>
							<Icon name="chevron-down" className="h-3 w-3 -rotate-90 text-slate-300" />
							<span className="text-[12px] font-medium text-slate-600">{currentGuide.title}</span>
						</div>
					</div>
				)}

				<div className="docs-container mx-auto w-full max-w-4xl px-6 py-10 md:py-14 lg:px-12">
					<Outlet />

					{/* Navigation Buttons */}
					{(prevGuide || nextGuide) && (
						<div className="mt-16 grid grid-cols-2 gap-4 border-t border-slate-200/60 pt-10">
							{prevGuide ? (
								<Link
									to={`/guias/${prevGuide.to}`}
									className="group flex flex-col rounded-xl border border-slate-200/60 bg-white p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-300 hover:border-slate-300/80 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
								>
									<div className="mb-3 flex items-center gap-1.5 text-slate-400">
										<Icon
											name="arrow-left"
											className="h-3 w-3 transition-transform duration-300 group-hover:-translate-x-1"
										/>
										<span className="text-[11px] font-semibold uppercase tracking-wider">Anterior</span>
									</div>
									<span className="text-[11px] font-medium text-slate-400">{prevGuide.section}</span>
									<span className="mt-0.5 text-[14px] font-semibold text-slate-700 transition-colors duration-200 group-hover:text-primary">
										{prevGuide.title}
									</span>
								</Link>
							) : (
								<div />
							)}

							{nextGuide ? (
								<Link
									to={`/guias/${nextGuide.to}`}
									className="group flex flex-col items-end rounded-xl border border-slate-200/60 bg-white p-5 text-right shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-300 hover:border-slate-300/80 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
								>
									<div className="mb-3 flex items-center gap-1.5 text-slate-400">
										<span className="text-[11px] font-semibold uppercase tracking-wider">Siguiente</span>
										<Icon
											name="arrow-right"
											className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-1"
										/>
									</div>
									<span className="text-[11px] font-medium text-slate-400">{nextGuide.section}</span>
									<span className="mt-0.5 text-[14px] font-semibold text-slate-700 transition-colors duration-200 group-hover:text-primary">
										{nextGuide.title}
									</span>
								</Link>
							) : (
								<div />
							)}
						</div>
					)}
				</div>
			</main>
		</div>
	)
}
